/* eslint-disable react-hooks/exhaustive-deps */
import { Icon, IconButton, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import ChartsEmbeddedSDK from '@mongodb-js/charts-embed-dom'
import moment from 'moment'
import { unparse } from 'papaparse'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ChartExportSelector from './ChartExportSelector'
import DashboardFilters from './DashboardFilters'
import { getToken, loadFiltersFromLocalStorage } from './atlasHelper'

const styles = () => ({
  atlasDashboardWrapper: {
    height: 'calc(100vh - 140px)',
    position: 'relative',
  },
  atlasDashboardTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 2.4rem',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '2rem',
    padding: '0 2rem 0.5rem 2rem',
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 2rem 0.5rem 2rem',
    gap: '0.25rem',
  },
  helperText: {
    textAlign: 'right',
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: '2rem',
  },
})

function AtlasDashboardPage({ data, classes, api, history }) {
  const dashboardDiv = useRef(null)
  const [atlasDashboard, setAtlasDashboard] = useState(null)

  const [chartMap, setChartMap] = useState({})
  const [chartOptions, setChartOptions] = useState([])
  const [isChartsLoading, setChartsLoading] = useState(true)
  const [showUnnamedChartWarning, setShowUnnamedChartWarning] = useState(false)
  const [selectedChartId, setSelectedChartId] = useState('')

  const { baseUrl, dashboard } = data
  const { chartTitles, dashboardId } = dashboard

  const chartTitleMap = useMemo(() => {
    const map = new Map()
    if (chartTitles) {
      for (const { chartId, title } of chartTitles) {
        map.set(chartId, title)
      }
    }
    return map
  }, [chartTitles])

  const sdk = useMemo(
    () =>
      new ChartsEmbeddedSDK({
        baseUrl,
        getUserToken: async () => getToken(api),
        showAttribution: false,
      }),
    [baseUrl, api],
  )

  useEffect(() => {
    let mounted = true

    async function renderDashboard() {
      try {
        const dash = sdk.createDashboard({
          background: 'transparent',
          dashboardId,
          widthMode: 'scale',
          filter: loadFiltersFromLocalStorage(dashboardId) || {},
        })
        await dash.render(dashboardDiv.current)
        if (mounted) setAtlasDashboard(dash)
      } catch (err) {
        console.error('Dashboard render failed:', err)
      }
    }

    renderDashboard()
    return () => {
      mounted = false
    }
  }, [sdk, dashboardId])

  useEffect(() => {
    if (!atlasDashboard) return
    let mounted = true

    setChartsLoading(true)
    atlasDashboard
      .getAllCharts()
      .then((charts) => {
        if (!mounted) return
        const map = {}
        const options = charts.map((chart) => {
          map[chart.chartId] = chart
          const label = chartTitleMap.get(chart.chartId) || `Chart (${chart.chartId.slice(-12)})`
          return { id: chart.chartId, label }
        })
        setChartMap(map)
        setChartOptions(options)
        const shouldShowWarning = options.some((option) => option.label.startsWith('Chart ('))
        if (mounted) {
          setShowUnnamedChartWarning(shouldShowWarning)
        }
      })
      .catch((err) => console.error('Error loading charts:', err))
      .finally(() => {
        if (mounted) setChartsLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [atlasDashboard, chartTitleMap])

  const exportChartToCSV = useCallback(
    async (chartId) => {
      const chart = chartMap[chartId]
      if (!chart) return

      try {
        const { documents, fields } = await chart.getData()
        if (!documents?.length) {
          alert('No data available to export for this chart.')
          return
        }

        const formattedDocs = documents.map((doc) => {
          const result = {}
          for (const key in doc) {
            const header = fields && typeof fields[key] === 'string' ? fields[key] : key
            result[header] = doc[key]
          }
          return result
        })

        const csv = unparse(formattedDocs)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const timestamp = moment().format('YYYY-MM-DD')
        const title = chartTitleMap.get(chartId)
        const baseName = title || `chart_${chartId}`
        const safeBaseName = baseName.replace(/[\s/]/g, '_')
        a.href = url
        a.download = `${safeBaseName}_${timestamp}.csv`
        a.click()
        URL.revokeObjectURL(url)
        setSelectedChartId('')
      } catch (err) {
        console.error('Export failed for chart:', chartId, err)
        alert('Failed to export chart data.')
      }
    },
    [chartMap, chartTitleMap],
  )

  return (
    <div className={classes.atlasDashboardWrapper}>
      <Typography className={classes.atlasDashboardTitle} component="div">
        <IconButton onClick={() => history.push('/atlas-dashboards')}>
          <Icon>arrow_back</Icon>
        </IconButton>
        <div>
          <Typography variant="h4">{data.dashboard.name}</Typography>
          <Typography variant="subtitle1">{data.dashboard.subtitle}</Typography>
        </div>
      </Typography>

      <div className={classes.controlsContainer}>
        <div className={classes.controlsRow}>
          <DashboardFilters dashboard={atlasDashboard} dashboardId={data.dashboard.dashboardId} />
          <ChartExportSelector
            chartOptions={chartOptions}
            isLoading={isChartsLoading}
            selectedValue={selectedChartId}
            onSelectionChange={setSelectedChartId}
            onExport={(id) => exportChartToCSV(id)}
          />
        </div>
        {showUnnamedChartWarning && (
          <Typography variant="caption" className={classes.helperText}>
            Charts labeled 'Chart(...)' require a title. Please update the dashboard configuration.
          </Typography>
        )}
      </div>
      <div ref={dashboardDiv} style={{ height: '100%' }} />
    </div>
  )
}

export default withStyles(styles)(AtlasDashboardPage)
